// src/components/lessons/renderers/AccordionRenderer.jsx
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

const AccordionRenderer = ({ block, isMobile }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const createMarkup = (htmlString) => {
    if (typeof htmlString !== 'string') return { __html: '' };
    const sanitized = htmlString.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600; color: #000000;">$1</strong>');
    return { __html: sanitized };
  };

  return (
    <div
      style={{
        background: '#FFFFFF',
        borderRadius: '0.75rem',
        border: '2px solid #000000',
        marginBottom: '1.5rem',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isHovered ? '0 8px 24px rgba(0, 0, 0, 0.12)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Accordion Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: isMobile ? '1rem 1.25rem' : '1.25rem 1.5rem',
          textAlign: 'left',
          fontWeight: '700',
          color: isOpen ? '#FFFFFF' : '#000000',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: isOpen ? '#000000' : '#FFFFFF',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          fontSize: isMobile ? '0.95rem' : '1.05rem',
          position: 'relative'
        }}
      >
        {/* Blue accent line when open */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            background: '#3A86FF'
          }} />
        )}

        <span style={{ flex: 1, paddingRight: '1rem', paddingLeft: isOpen ? '0.5rem' : 0 }}>
          {block.title}
        </span>

        {isOpen ? (
          <ChevronDown style={{
            width: '20px',
            height: '20px',
            flexShrink: 0,
            color: '#3A86FF',
            transition: 'transform 0.3s ease',
            transform: 'rotate(0deg)'
          }} />
        ) : (
          <ChevronRight style={{
            width: '20px',
            height: '20px',
            flexShrink: 0,
            color: '#000000',
            transition: 'transform 0.3s ease'
          }} />
        )}

        {/* Decorative dot on hover */}
        {!isOpen && isHovered && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: '#3A86FF',
            opacity: 0.8
          }} />
        )}
      </button>

      {/* Accordion Content */}
      {isOpen && (
        <div style={{
          padding: isMobile ? '1.25rem' : '1.5rem',
          borderTop: '2px solid #000000',
          background: '#FAFBFC'
        }}>
          {typeof block.content === 'string' ? (
  <p
    style={{
      color: '#000000',
      lineHeight: '1.7',
      fontSize: isMobile ? '0.9rem' : '0.95rem',
      margin: 0,
      opacity: 0.8
    }}
    dangerouslySetInnerHTML={createMarkup(block.content)}
  />
          ) : Array.isArray(block.content) ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {block.content.map((item, i) => (
                <div
                  key={i}
                  style={{
                    paddingBottom: i !== block.content.length - 1 ? '1.25rem' : 0,
                    borderBottom: i !== block.content.length - 1 ? '1px solid #E5E7EB' : 'none'
                  }}
                >
                  {item.function && (
  <p
    style={{
      fontWeight: '700',
      color: '#000000',
      marginBottom: '0.75rem',
      fontSize: isMobile ? '0.95rem' : '1rem'
    }}
    dangerouslySetInnerHTML={createMarkup(item.function)}
  />
)}
                  {item.redemittel && (
                    <p
                      style={{
                        color: '#000000',
                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                        marginBottom: '0.5rem',
                        lineHeight: '1.6',
                        opacity: 0.8
                      }}
                      dangerouslySetInnerHTML={createMarkup(item.redemittel)}
                    />
                  )}
                  {item.example && (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.75rem 1rem',
                      background: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderLeft: '3px solid #3A86FF',
                      borderRadius: '0.375rem'
                    }}>
                      <p
                        style={{
                          fontSize: isMobile ? '0.85rem' : '0.9rem',
                          color: '#000000',
                          margin: 0,
                          fontStyle: 'italic',
                          lineHeight: '1.5',
                          opacity: 0.7
                        }}
                        dangerouslySetInnerHTML={createMarkup(item.example)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              {Object.entries(block.content).map(([key, value]) => (
                <p key={key} style={{
                  color: '#000000',
                  fontSize: isMobile ? '0.9rem' : '0.95rem',
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  <strong style={{
                    textTransform: 'capitalize',
                    fontWeight: '700',
                    color: '#000000',
                    marginRight: '0.5rem'
                  }}>
                    {key}:
                  </strong>
                  <span
  style={{ opacity: 0.8 }}
  dangerouslySetInnerHTML={createMarkup(value)}
/>
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccordionRenderer;